import React from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import { Delete } from "@mui/icons-material";

export default function MatrixRowsEditor({ rows, columns, onUpdate, readOnly }) {
  // Local buffer to avoid saving on every keystroke
  const [localRows, setLocalRows] = React.useState(rows || []);

  React.useEffect(() => {
    setLocalRows(rows || []);
  }, [rows]);
  // Ensure every row has one cell per column, matched by column identity (id or code).
  const ensureCells = (r) => {
    // Only consider positive ids to avoid collisions when id is 0 for unsaved columns
    const byId = new Map((r.matrixCells || []).filter(x => (x.columnId ?? null) > 0).map(x => [x.columnId, x]));
    const byCode = new Map((r.matrixCells || []).filter(x => x.columnCode != null).map(x => [x.columnCode, x]));
    const cells = (columns || []).map(col =>
      ((col.id ?? 0) > 0 && byId.get(col.id)) || byCode.get(col.code) || {
        id: 0,
        columnId: (col.id ?? 0) > 0 ? col.id : null,
        columnCode: col.code,
        description: ""
      }
    );
    return { ...r, matrixCells: cells };
  };

  // Helper to find the cell for a given column in a given row
  const getCellForColumn = (r, col) => {
    const rc = r.matrixCells || [];
    // Prefer match by id when available (>0), else by code
    if ((col?.id ?? 0) > 0) {
      const found = rc.find(c => c.columnId === col.id);
      if (found) return found;
    }
    if (col?.code != null) {
      const found = rc.find(c => c.columnCode === col.code);
      if (found) return found;
    }
    return { id: 0, columnId: (col?.id ?? 0) > 0 ? col?.id : null, columnCode: col?.code, description: "" };
  };

  const addRow = () => {
    const base = ensureCells({
      id: 0, answer: "", order: (localRows?.length || 0) + 1, matrixCells: []
    });
    const next = [...(localRows || []), base];
    setLocalRows(next);
    // Discrete action -> persist immediately
    onUpdate(next);
  };

  const removeRow = (idx) => {
    const next = [...(localRows || [])];
    next.splice(idx, 1);
    // reordenar
    const re = next.map((r, i) => ({...r, order: i+1}));
    setLocalRows(re);
    // Discrete action -> persist immediately
    onUpdate(re);
  };

  const updateRowLocal = (idx, field, value) => {
    const next = [...(localRows || [])];
    next[idx] = ensureCells({ ...next[idx], [field]: value });
    setLocalRows(next);
  };

  const updateCellLocal = (rowIdx, col, value) => {
    const next = [...(localRows || [])];
    const r = ensureCells(next[rowIdx]);

    // Find index by identity (id first, then code)
  const idxById = (col?.id ?? 0) > 0 ? r.matrixCells.findIndex(c => c.columnId === col.id) : -1;
    const idxByCode = idxById === -1 && col?.code != null ? r.matrixCells.findIndex(c => c.columnCode === col.code) : -1;
    const colIdx = idxById !== -1 ? idxById : (idxByCode !== -1 ? idxByCode : -1);

    if (colIdx === -1) {
      // If for some reason the cell didn't exist, append it
      r.matrixCells = [
        ...r.matrixCells,
        { id: 0, columnId: (col?.id ?? 0) > 0 ? col?.id : null, columnCode: col?.code, description: value }
      ];
    } else {
      const c = r.matrixCells[colIdx];
      r.matrixCells[colIdx] = { ...c, description: value };
    }

    next[rowIdx] = r;
    setLocalRows(next);
  };

  const commit = () => {
    onUpdate(localRows);
  };

  return (
    <div className="card p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="m-0">Filas</h6>
        {!readOnly && <Button variant="outlined" onClick={addRow}>Agregar fila</Button>}
      </div>

      {(localRows || []).map((r, idx) => (
        <div key={r.id || idx} className="border rounded p-2 mb-3">
          <div className="d-flex">
            <TextField
              label="Título de la fila"
              variant="outlined"
              value={r.answer || ""}
              onChange={(e)=>updateRowLocal(idx, "answer", e.target.value)}
              onBlur={commit}
              fullWidth
              disabled={readOnly}
            />
            {!readOnly && (
              <IconButton onClick={()=>removeRow(idx)}><Delete/></IconButton>
            )}
          </div>

          <div className="row mt-2">
            {(columns || []).map((col, cIdx) => {
              const cell = getCellForColumn(r, col);
              return (
                <div className="col-md-6 mb-2" key={col.id ?? col.code ?? cIdx}>
                  <TextField
                    label={`Descripción ${col.label}`}
                    variant="outlined"
                    multiline rows={3} fullWidth
                    value={cell.description || ""}
                    onChange={(e) => updateCellLocal(idx, col, e.target.value)}
                    onBlur={commit}
                    disabled={readOnly}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
